import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { Add as AddIcon, ArrowBack as BackIcon, Close as CloseIcon } from '@mui/icons-material'
import { smbsApi, SMBData } from '../../services/api'
import { MAX_DOCUMENT_SIZE_LABEL } from '../../constants/uploads'
import { useNotification } from '../../contexts/NotificationContext'
import { validatePdfFile } from '../../utils/uploads'
import { extractErrorMessage } from '../../utils/errorUtils'
import DocumentFilePickerButton from '../DocumentFilePickerButton'

interface AddSMBDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: (smb: SMBData, scrapingPromise?: Promise<any>) => void
}

const businessTypes = [
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'dental', label: 'Dental' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'beauty', label: 'Beauty & Spa' },
  { value: 'legal', label: 'Legal Services' },
  { value: 'retail', label: 'Retail' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'other', label: 'Other' },
]

const industries = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'resort', label: 'Resort' },
  { value: 'clinic', label: 'Medical Clinic' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'gym', label: 'Gym' },
  { value: 'yoga', label: 'Yoga Studio' },
  { value: 'dental_clinic', label: 'Dental Clinic' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'spa', label: 'Spa & Wellness' },
  { value: 'salon', label: 'Beauty Salon' },
  { value: 'law_firm', label: 'Law Firm' },
  { value: 'retail_store', label: 'Retail Store' },
  { value: 'coaching', label: 'Life/Business Coaching' },
  { value: 'other', label: 'Other' },
]

const AddSMBDialog: React.FC<AddSMBDialogProps> = ({ open, onClose, onSuccess }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeStep, setActiveStep] = useState(0)
  const [createdSMBId, setCreatedSMBId] = useState<string | null>(null)
  const [step1Data, setStep1Data] = useState({
    name: '',
    business_type: '',
    industry: '',
    owner_name: '',
    owner_email: '',
    phone: '',
  })
  const [initialStep1Data, setInitialStep1Data] = useState(step1Data)
  const [step2Data, setStep2Data] = useState({
    website: '',
    priority: 'none' as 'none' | 'website' | 'document',
  })
  const [documentFile, setDocumentFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { showNotification } = useNotification()

  const handleStep1Change = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setStep1Data(prev => ({
      ...prev,
      [field]: event.target.value
    }))
    if (error) setError(null)
  }

  const handleStep2Change = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setStep2Data(prev => ({
      ...prev,
      [field]: event.target.value
    }))
    if (error) setError(null)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validationError = validatePdfFile(file)
    if (validationError) {
      setError(validationError)
      event.target.value = ''
      return
    }
    setDocumentFile(file)
    setError(null)
  }

  const handleStep1Submit = async () => {
    // Validate required fields
    if (!step1Data.name || !step1Data.business_type || !step1Data.industry || 
        !step1Data.owner_name || !step1Data.owner_email || !step1Data.phone) {
      setError('Please fill in all required fields')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(step1Data.owner_email)) {
      setError('Please enter a valid email address')
      return
    }

    if (createdSMBId && JSON.stringify(step1Data) === JSON.stringify(initialStep1Data)) {
      setActiveStep(1)
      return
    }

    setLoading(true)
    setError(null)

    try {
        const response = await smbsApi.createUpdateSMB(step1Data as any)
        const newSMB = (response as any)?.smb ?? response
        setCreatedSMBId(newSMB.id)
        setInitialStep1Data(step1Data)
        setActiveStep(1)
    } catch (err: any) {
      console.error('Error saving business:', err)
      setError(extractErrorMessage(err, 'Failed to save business'))
    } finally {
      setLoading(false)
    }
  }

  
  const resetAndClose = () => {
    setActiveStep(0)
    setCreatedSMBId(null)
    setStep1Data({
      name: '',
      business_type: '',
      industry: '',
      owner_name: '',
      owner_email: '',
      phone: '',
    })
    setInitialStep1Data({
      name: '',
      business_type: '',
      industry: '',
      owner_name: '',
      owner_email: '',
      phone: '',
    })
    setStep2Data({
      website: '',
      priority: 'none',
    })
    setDocumentFile(null)
    setError(null)
    setLoading(false)
    onClose()
  }

  const handleStep2Submit = async () => {
    if (!createdSMBId) return

    setLoading(true)
    setError(null)

    try {
      const payload: any = {}
      const hasWebsite = !!step2Data.website
      const hasDocument = !!documentFile
      
      // Add website if provided
      if (hasWebsite) {
        payload.website = step2Data.website
      }
      
      // Smart priority logic
      if (hasWebsite && hasDocument) {
        if (step2Data.priority !== 'none') {
          payload.priority = step2Data.priority
        }
      } else if (hasWebsite) {
        payload.priority = 'website'
      } else if (hasDocument) {
        payload.priority = 'document'
      }

      // Update SMB with knowledge sources (and upload PDF if selected) via the same create_update_smb endpoint
      const combinedPayload = { ...payload, smb_id: createdSMBId }
      const updatePromise =
        documentFile
          ? smbsApi.createUpdateSMB(combinedPayload, { file: documentFile })
          : Object.keys(payload).length > 0
            ? smbsApi.createUpdateSMB(combinedPayload)
            : undefined

      const finalSMB = Object.keys(payload).length > 0
        ? { id: createdSMBId, ...step1Data, ...payload }
        : { id: createdSMBId, ...step1Data }

      if (updatePromise) {
        if (documentFile) {
          showNotification('Uploading business document…', 'info')
          void updatePromise
            .then(() => showNotification('Business document uploaded successfully!', 'success'))
            .catch((err: any) => {
              console.error('Error uploading SMB document:', err)
              showNotification(extractErrorMessage(err, 'Failed to upload business document'), 'error')
            })
        }
        onSuccess(finalSMB as SMBData, updatePromise)
      } else {
        onSuccess(finalSMB as SMBData)
      }
      
      resetAndClose()

    } catch (err: any) {
      console.error('Error configuring knowledge sources:', err)
      setError(extractErrorMessage(err, 'Failed to configure knowledge sources'))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (loading) return

    // If step 1 is done, save the progress before closing
    if (createdSMBId) {
      const finalSMB = { id: createdSMBId, ...step1Data }
      onSuccess(finalSMB as SMBData) 
    }
    
    resetAndClose()
  }


  const handleBack = () => {
    setActiveStep(0)
    setError(null)
  }

  // Render Step 1: Business Details
  const renderStep1 = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Business Name"
          value={step1Data.name}
          onChange={handleStep1Change('name')}
          required
          placeholder="e.g., Sunset Resort & Spa"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="Business Type"
          value={step1Data.business_type}
          onChange={handleStep1Change('business_type')}
          required
        >
          {businessTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="Industry"
          value={step1Data.industry}
          onChange={handleStep1Change('industry')}
          required
        >
          {industries.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Owner Name"
          value={step1Data.owner_name}
          onChange={handleStep1Change('owner_name')}
          required
          placeholder="e.g., John Smith"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contact Email"
          type="email"
          value={step1Data.owner_email}
          onChange={handleStep1Change('owner_email')}
          required
          placeholder="owner@business.com"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Phone Number"
          value={step1Data.phone}
          onChange={handleStep1Change('phone')}
          required
          placeholder="+1 (555) 123-4567"
        />
      </Grid>
    </Grid>
  )

  // Render Step 2: Knowledge Sources
  const renderStep2 = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure how we learn about your business (optional)
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Website URL (Optional)"
          value={step2Data.website}
          onChange={handleStep2Change('website')}
          placeholder="https://www.business.com"
          helperText="We'll automatically extract business information"
        />
      </Grid>

      <Grid item xs={12}>
        <DocumentFilePickerButton
          file={documentFile}
          onChange={handleFileChange}
          disabled={loading}
          emptyLabel="📄 Upload Document (Optional)"
          helperText={`Supported: PDF (Max ${MAX_DOCUMENT_SIZE_LABEL})`}
        />
      </Grid>

      {step2Data.website && documentFile && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Priority"
            value={step2Data.priority}
            onChange={handleStep2Change('priority')}
            helperText="Which source should take precedence?"
          >
            <MenuItem value="none">None (use both equally)</MenuItem>
            <MenuItem value="website">Website</MenuItem>
            <MenuItem value="document">Document</MenuItem>   
          </TextField>
        </Grid>
      )}
    </Grid>
  )

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: isMobile ? 'auto' : '600px' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="primary" />
            Add New Business - Step {activeStep + 1} of 2
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ px: 3, pt: 2 }}>
        <Step>
          <StepLabel>Business Details</StepLabel>
        </Step>
        <Step>
          <StepLabel>Knowledge Sources</StepLabel>
        </Step>
      </Stepper>

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, justifyContent: 'space-between' }}>
        {activeStep === 1 ? (
          <Button
            onClick={handleBack}
            disabled={loading}
            startIcon={<BackIcon />}
            color="inherit"
          >
            Back
          </Button>
        ) : (
          <Box />
        )}
        <Button
          variant="contained"
          onClick={activeStep === 0 ? handleStep1Submit : handleStep2Submit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
          sx={{ minWidth: 140 }}
        >
          {loading ? 'Processing...' : activeStep === 0 ? 'Save & Continue' : 'Finish'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AddSMBDialog
